/**
 * TanStack Query hooks for Projects.
 */
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Project,
  ProjectCloseInput,
  ProjectCreateInput,
  ProjectListQuery,
  ProjectListResponse,
  ProjectApproveInput,
  ProjectBidUpdateInput,
  ProjectRecapResponse,
  ProjectRequestChangesInput,
  ProjectResubmitInput,
  ProjectRescheduleInput,
  ProjectUpdateInput,
  RequestReviewList,
  PickupSuggestionsResponse,
} from '@vari-crew/schemas';
import { apiCall, apiFetchBlob } from '@/lib/api';

const KEY = ['projects'] as const;

/**
 * Fetch the recap PDF through the authenticated client and open it in a new
 * tab via an object URL. The endpoint is auth-gated, so a plain <a href> /
 * window.open can't reach it under real MSAL (no Authorization header) — that
 * 401'd in the deployed env. Returns once the blob is loaded.
 */
export async function openRecapPdf(projectId: string): Promise<void> {
  const blob = await apiFetchBlob(`/projects/${projectId}/recap.pdf`);
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  // Revoke after a delay so the new tab has time to load the blob.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function queryString(q: ProjectListQuery): string {
  const params = new URLSearchParams();
  if (q.limit !== undefined) params.set('limit', String(q.limit));
  if (q.offset !== undefined) params.set('offset', String(q.offset));
  if (q.status) params.set('status', q.status);
  if (q.pm_user_id) params.set('pm_user_id', q.pm_user_id);
  if (q.priority) params.set('priority', q.priority);
  if (q.q) params.set('q', q.q);
  if (q.from_date) params.set('from_date', q.from_date);
  if (q.to_date) params.set('to_date', q.to_date);
  if (q.sort) params.set('sort', q.sort);
  if (q.order) params.set('order', q.order);
  const s = params.toString();
  return s ? `?${s}` : '';
}

/** Shared query options so a prefetch warms the exact entry the hook reads. */
export function projectsListOptions(query: ProjectListQuery = { limit: 50, offset: 0 }) {
  return queryOptions({
    queryKey: [...KEY, 'list', query],
    queryFn: () => apiCall<ProjectListResponse>(`/projects${queryString(query)}`),
  });
}

export function useProjects(query: ProjectListQuery = { limit: 50, offset: 0 }) {
  return useQuery(projectsListOptions(query));
}

/**
 * Enriched supervisor request-review queue (EX.8): every pending request with
 * its labor-vs-budget estimate, back-orders, readiness flags, and doc/task
 * counts — the signals to approve with eyes open.
 */
export function useRequestReviews(enabled = true) {
  return useQuery<RequestReviewList>({
    queryKey: [...KEY, 'requests', 'review'],
    enabled,
    queryFn: () => apiCall<RequestReviewList>('/projects/requests/review'),
  });
}

/**
 * Days other projects already have a pickup scheduled (demo #7, F20) — for
 * consolidating this project's pickup onto an existing truck run.
 */
export function usePickupSuggestions(id: string, enabled = true) {
  return useQuery<PickupSuggestionsResponse>({
    queryKey: [...KEY, 'detail', id, 'pickup-suggestions'],
    enabled: enabled && !!id,
    queryFn: () => apiCall<PickupSuggestionsResponse>(`/projects/${id}/pickup-suggestions`),
  });
}

/** Supervisor sends a request back to the PM for revision (SCH.5). */
export function useRequestChanges(id: string) {
  const qc = useQueryClient();
  return useMutation<Project, Error, ProjectRequestChangesInput>({
    mutationFn: (body) =>
      apiCall<Project>(`/projects/${id}/request-changes`, { method: 'PATCH', body }),
    onSuccess: (updated) => {
      qc.setQueryData([...KEY, 'detail', id], updated);
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/** Supervisor moves a scheduled project to new days/hours (calendar drag,
 *  Phase 2) — re-seeds tasks + notifies requestor/crew, no re-approval. */
export function useRescheduleProject(id: string) {
  const qc = useQueryClient();
  return useMutation<Project, Error, ProjectRescheduleInput>({
    mutationFn: (body) =>
      apiCall<Project>(`/projects/${id}/reschedule`, { method: 'PATCH', body }),
    onSuccess: (updated) => {
      qc.setQueryData([...KEY, 'detail', id], updated);
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/** PM revises a sent-back request and resubmits it (SCH.5). */
export function useResubmitProject(id: string) {
  const qc = useQueryClient();
  return useMutation<Project, Error, ProjectResubmitInput>({
    mutationFn: (body) =>
      apiCall<Project>(`/projects/${id}/resubmit`, { method: 'PATCH', body }),
    onSuccess: (updated) => {
      qc.setQueryData([...KEY, 'detail', id], updated);
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/** Shared query options for a single project — used by the hook and row-hover prefetch. */
export function projectDetailOptions(id: string | undefined) {
  return queryOptions({
    queryKey: [...KEY, 'detail', id],
    enabled: !!id,
    queryFn: () => apiCall<Project>(`/projects/${id}`),
  });
}

export function useProject(id: string | undefined) {
  return useQuery(projectDetailOptions(id));
}

export function useProjectRecap(id: string | undefined) {
  return useQuery<ProjectRecapResponse>({
    queryKey: [...KEY, 'recap', id],
    enabled: !!id,
    queryFn: () => apiCall<ProjectRecapResponse>(`/projects/${id}/recap`),
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation<Project, Error, ProjectCreateInput>({
    mutationFn: (body) => apiCall<Project>('/projects', { method: 'POST', body }),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.setQueryData([...KEY, 'detail', created.id], created);
    },
  });
}

export function useUpdateProject(id: string) {
  const qc = useQueryClient();
  return useMutation<Project, Error, ProjectUpdateInput>({
    mutationFn: (body) => apiCall<Project>(`/projects/${id}`, { method: 'PATCH', body }),
    onSuccess: (updated) => {
      qc.setQueryData([...KEY, 'detail', id], updated);
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/** PATCH /projects/:id/cancel — supervisor/admin calls the project off (terminal). */
export function useCancelProject(id: string) {
  const qc = useQueryClient();
  return useMutation<Project, Error, void>({
    mutationFn: () => apiCall<Project>(`/projects/${id}/cancel`, { method: 'PATCH' }),
    onSuccess: (updated) => {
      qc.setQueryData([...KEY, 'detail', id], updated);
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/** DELETE /projects/:id — soft delete (restorable from the Deleted projects area). */
export function useDeleteProject(id: string) {
  const qc = useQueryClient();
  return useMutation<Project, Error, void>({
    mutationFn: () => apiCall<Project>(`/projects/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/** POST /projects/:id/restore — bring a soft-deleted project back. */
export function useRestoreProject(id: string) {
  const qc = useQueryClient();
  return useMutation<Project, Error, void>({
    mutationFn: () => apiCall<Project>(`/projects/${id}/restore`, { method: 'POST' }),
    onSuccess: (updated) => {
      qc.setQueryData([...KEY, 'detail', id], updated);
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/** GET /projects/deleted — soft-deleted projects (supervisor/admin restore surface). */
export function useDeletedProjects(enabled = true) {
  return useQuery<ProjectListResponse>({
    queryKey: [...KEY, 'list', 'deleted'],
    queryFn: () => apiCall<ProjectListResponse>('/projects/deleted'),
    enabled,
  });
}

/** PATCH /projects/:id/approve — supervisor approves a requested project. */
export function useUpdateBid(id: string) {
  const qc = useQueryClient();
  return useMutation<Project, Error, ProjectBidUpdateInput>({
    mutationFn: (body) => apiCall<Project>(`/projects/${id}/bid`, { method: 'PATCH', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useSendBid(id: string) {
  return useMutation<{ ok: boolean; stub: boolean; sent_to: string }, Error, void>({
    mutationFn: () =>
      apiCall<{ ok: boolean; stub: boolean; sent_to: string }>(`/projects/${id}/send-bid`, {
        method: 'POST',
      }),
  });
}

/** Approve / request a change to the bid from inside the app (§A6) — the
 *  authenticated counterpart to the public token page. */
export function useBidApproval(id: string) {
  const qc = useQueryClient();
  return useMutation<
    { ok: boolean; status: string; approved: boolean },
    Error,
    { action: 'approve' | 'feedback'; note?: string }
  >({
    mutationFn: (body) =>
      apiCall<{ ok: boolean; status: string; approved: boolean }>(
        `/projects/${id}/bid-approval`,
        { method: 'POST', body },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useApproveProject(id: string) {
  const qc = useQueryClient();
  return useMutation<Project, Error, ProjectApproveInput | void>({
    mutationFn: (body) =>
      apiCall<Project>(`/projects/${id}/approve`, {
        method: 'PATCH',
        body: body ?? {},
      }),
    onSuccess: (updated) => {
      qc.setQueryData([...KEY, 'detail', id], updated);
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/** POST /projects/:id/bid-email-preview — render (not send) the bid approval
 *  email so the supervisor can preview it before approving (demo #5). Refetches
 *  as the draft schedule / pickup change (keyed on the body). */
export function useBidEmailPreview(
  id: string,
  body: ProjectApproveInput | null,
  enabled: boolean,
) {
  return useQuery<{ subject: string; html: string }>({
    queryKey: [...KEY, 'bid-email-preview', id, body],
    queryFn: () =>
      apiCall<{ subject: string; html: string }>(`/projects/${id}/bid-email-preview`, {
        method: 'POST',
        body: body ?? {},
      }),
    enabled: enabled && !!body,
    staleTime: 30_000,
  });
}

/** PATCH /projects/:id/propose — supervisor proposes a schedule the PM must
 *  confirm (stays 'requested' until the PM accepts). */
export function useProposeSchedule(id: string) {
  const qc = useQueryClient();
  return useMutation<Project, Error, ProjectApproveInput>({
    mutationFn: (body) =>
      apiCall<Project>(`/projects/${id}/propose`, { method: 'PATCH', body }),
    onSuccess: (updated) => {
      qc.setQueryData([...KEY, 'detail', id], updated);
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/** PATCH /projects/:id/confirm-schedule — the PM (or supervisor) confirms the
 *  proposed schedule, committing it (→ scheduled, tasks seeded). */
export function useConfirmSchedule(id: string) {
  const qc = useQueryClient();
  return useMutation<Project, Error, void>({
    mutationFn: () => apiCall<Project>(`/projects/${id}/confirm-schedule`, { method: 'PATCH' }),
    onSuccess: (updated) => {
      qc.setQueryData([...KEY, 'detail', id], updated);
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ['dispatch'] });
    },
  });
}

/** POST /projects/:id/recap/draft — Gemini-assembled closeout narrative
 * (Pillar D.4). Returns a DRAFT for the editable textarea; never auto-saves. */
export function useDraftRecapNarrative(id: string) {
  return useMutation<{ narrative: string; source: 'stub' | 'gemini' }, Error, void>({
    mutationFn: () =>
      apiCall<{ narrative: string; source: 'stub' | 'gemini' }>(
        `/projects/${id}/recap/draft`,
        { method: 'POST' },
      ),
  });
}

/** POST /projects/:id/close — marks status=complete, sets completed_at, and
 * optionally persists the reviewed recap narrative. */
export function useCloseProject(id: string) {
  const qc = useQueryClient();
  return useMutation<Project, Error, ProjectCloseInput | void>({
    mutationFn: (input) =>
      apiCall<Project>(`/projects/${id}/close`, { method: 'POST', body: input ?? {} }),
    onSuccess: (updated) => {
      qc.setQueryData([...KEY, 'detail', id], updated);
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ['dispatch'] });
      qc.invalidateQueries({ queryKey: ['public-status'] });
    },
  });
}
